// Sign in. Where the environment configures AuthKit, "Continue with WarmHub"
// leaves for WarmHub's sign-in and comes back to /callback. Where it doesn't,
// sign-in isn't configured: the page says so and no one can sign in, because
// every Consumer API request needs a token. WarmHub's sign-in page offers
// its own methods, and sign-up where the environment allows it. See Design >
// Console, "Sign-in".

import { Button, Notice } from "../../../../ui/src/index.ts";
import { signIn, signOut } from "../actions.ts";
import { useStore } from "../store.ts";
import { PublicFrame } from "./Landing.tsx";

/**
 * The card's buttons. After a refusal, "Continue with WarmHub" would sign the
 * same account straight back in through its WorkOS session, so sign-out is the
 * only action. After another reason, such as an expired token, signing in is
 * primary and signing out stays available.
 */
export function SignInActions({ real, reason, refused, onSignIn, onSignOut }: { real: boolean; reason: string | null; refused: boolean; onSignIn: () => void; onSignOut: () => void }) {
  const out = (variant: "solid" | "ghost") => <Button variant={variant} onClick={onSignOut}>Sign out of WarmHub to use another account</Button>;
  if (refused) return out("solid");
  return <>
    <Button variant="solid" onClick={onSignIn} disabled={!real}>Continue with WarmHub</Button>
    {reason && out("ghost")}
  </>;
}

export function SignIn() {
  const real = useStore((s) => s.config.authkit !== null);
  // Why the Exchange ended the last session, such as a token that expired or
  // sign-up that isn't open. Signing in again can't help with a refusal, so
  // for one the page makes signing out of WarmHub the primary action.
  const reason = useStore((s) => s.signedOutReason);
  const refused = useStore((s) => s.signedOutRefused);
  return (
    <PublicFrame>
      <div class="cs-public__inner cs-signin">
        <section class="cs-signin__pitch">
          <h1 class="cs-display">Answers from <strong>curated data</strong>,<br />with their sources.</h1>
          <ul class="cs-signin__points">
            <li><span>01</span>Try questions in the Playground, with guided lessons.</li>
            <li><span>02</span>Pay only for answers, at the hub's ask, and see every charge in Credits.</li>
            <li><span>03</span>Take the same request to your code with your WarmHub token.</li>
          </ul>
        </section>
        <section class="cs-signin__card">
          <div>
            <h2>Sign in to the Console</h2>
            <p>The Console uses your WarmHub account. You'll sign in with WarmHub and come back here.</p>
          </div>
          {!real && <Notice tone="neutral" title="Sign-in isn't configured in this environment yet" />}
          {reason && <Notice tone={refused ? "warning" : "info"} title={reason} live />}
          <div class="cs-signin__actions">
            <SignInActions real={real} reason={reason} refused={refused} onSignIn={signIn} onSignOut={signOut} />
          </div>
        </section>
      </div>
    </PublicFrame>
  );
}
